import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text, VStack, Divider, Spinner, Center, Alert, AlertIcon, AlertDescription } from '@chakra-ui/react';
import { gradeBenchPanelActions, useHandleRubricAnswerChange, spanSelectors } from '@codametrix/ui-common';
import { pxToRem } from '@codametrix/themes/chakra/biome';
import { RubricQuestionField } from '../highlight-drawer/rubric-question-field';
import {
  getWorkflowForCodeType,
  findAnswerForQuestion,
  codeTypeToEntityType,
  getQuestionIdsToClear,
} from '../highlight-drawer/rubric-utils';
import { useRubricAutofill } from '../highlight-drawer/useRubricAutofill';
import {
  useWorkflowEvaluation,
  EvaluateWorkflowFn,
} from '../highlight-drawer/useWorkflowEvaluation';
import './styles/index.scss';
export interface CaseQuestionsFormProps {
  // The workitem ID (t_work_item.id) used as entityId for CASE attributes
  sdWorkitemId: string;
  // Function to call the workflow evaluation API
  evaluateFn?: EvaluateWorkflowFn;
  // Work item ID (t_work_item.id) for system question expression evaluation
  workItemId?: number;
  // Optional disabled state
  isDisabled?: boolean;
}

/**
 * CaseQuestionsForm displays case-level questions from the rubric's CASE workflow.
 * It uses the same RubricQuestionField component as the highlight drawer for consistent styling.
 * Questions are displayed with trigger-based sequencing (GO_TO, STOP actions).
 *
 * Attributes are fetched centrally via fetchAllAttributes when the case loads.
 * This component reads caseAttributes from Redux state.
 */
export const CaseQuestionsForm: React.FC<CaseQuestionsFormProps> = ({
  sdWorkitemId,
  evaluateFn,
  workItemId,
  isDisabled = false
}) => {
  const dispatch = useDispatch();

  const rubricVersion = useSelector(
    (state: any) => state.gradeBenchPanel?.rubricVersion
  ) as CQA.FullRubricVersion | null | undefined;

  const caseAttributes = useSelector(
    (state: any) => state.gradeBenchPanel?.caseAttributes
  ) as CQA.Attribute[] | undefined;

  const attributesFetched = useSelector(
    (state: any) => state.gradeBenchPanel?.attributesFetched
  ) as boolean | undefined;

  const attributes = caseAttributes || [];
  const allSpans = useSelector(
    (state: any) => spanSelectors.selectSpanAnnotations(state.gradeBenchPanel)
  ) as Accelerate.SpanAnnotation[];
  const isAttributesLoading = !attributesFetched;

  const caseWorkflow = React.useMemo(
    () => getWorkflowForCodeType(rubricVersion, 'case'),
    [rubricVersion]
  );

  const {
    evaluation,
    visibleQuestions,
    filteredAnswersMap,
    autofilledAnswersMap,
    caseLevelComplete,
    isLoading: isEvaluationLoading,
    error,
  } = useWorkflowEvaluation({
    workflowId: caseWorkflow?.id,
    attributes,
    workflowQuestions: caseWorkflow?.questions,
    workItemId,
    evaluateFn: evaluateFn!,
    enabled: !!evaluateFn && !!caseWorkflow?.id,
  });

  useEffect(() => {
    dispatch(gradeBenchPanelActions.setCaseLevelComplete(caseLevelComplete));
  }, [caseLevelComplete, dispatch]);

  const [lastChangedQuestionId, setLastChangedQuestionId] = useState<number | null>(null);

  const handleAttributeChange = useCallback(
    (params: CQA.UpdateAttributeParams) => {
      dispatch(gradeBenchPanelActions.updateAttribute(params) as any);
    },
    [dispatch]
  );

  const handleDeleteAttribute = useCallback(
    async (params: CQA.DeleteAttributeParams) => {
      await dispatch(gradeBenchPanelActions.deleteAttribute(params) as any);
    },
    [dispatch]
  );

  const handleClearDownstream = useCallback(
    (params: CQA.ClearDownstreamAttributesParams) => {
      dispatch(gradeBenchPanelActions.clearDownstreamAttributes(params));
    },
    [dispatch]
  );

  const handleAnswerChangeWithCleanup = useHandleRubricAnswerChange(
    handleAttributeChange,
    handleDeleteAttribute,
    handleClearDownstream
  );

  // Use a ref to break the circular dependency between hook and handler
  const handleAnswerChangeRef = useRef<(questionId: number, value: number | number[] | string) => void>(() => {});

  // Use the autofill hook to handle autofill logic (only for multiple-choice; FREE_TEXT has no autofill)
  const { trackAnswerChange, clearDownstreamTracking } = useRubricAutofill({
    entityId: sdWorkitemId,
    entityKey: `case-${sdWorkitemId}`,
    attributes,
    autofilledAnswers: autofilledAnswersMap,
    onAutofill: (questionId, answerValueId) => {
      handleAnswerChangeRef.current(questionId, answerValueId);
    }
  });

  // Handle answer change for a question. value: number = answerValueId (MULTIPLE_CHOICE/SYSTEM), number[] = spanAnnotationIds (SPAN/SPANS), string = answerDisplayText (FREE_TEXT)
  const handleAnswerChange = useCallback(
    async (questionId: number, value: number | number[] | string) => {
      if (!caseWorkflow?.questions) return;

      setLastChangedQuestionId(questionId);

      const downstreamQuestionIds = getQuestionIdsToClear(
        visibleQuestions,
        attributes,
        questionId
      );

      const isFreeText = typeof value === 'string';
      if (!isFreeText && typeof value === 'number') {
        trackAnswerChange(questionId, value);
      }
      clearDownstreamTracking(downstreamQuestionIds);

      // Determine which answer field to use based on question type
      const question = visibleQuestions.find(wq => wq.question.id === questionId)?.question;
      const isSpan = question?.questionType === 'SPAN';
      const isSpans = question?.questionType === 'SPANS';

      let answerFields: Record<string, unknown>;
      if (isFreeText) {
        answerFields = { answerDisplayText: value };
      } else if (isSpan || isSpans) {
        answerFields = { spanAnnotationIds: value };
      } else {
        answerFields = { answerValueId: value };
      }

      await handleAnswerChangeWithCleanup({
        entityId: sdWorkitemId,
        entityType: codeTypeToEntityType('case'),
        attributes,
        questionId,
        ...answerFields,
        downstreamQuestionIds
      });
    },
    [caseWorkflow, attributes, visibleQuestions, sdWorkitemId, handleAnswerChangeWithCleanup, trackAnswerChange, clearDownstreamTracking]
  );

  handleAnswerChangeRef.current = handleAnswerChange;

  if (!rubricVersion) {
    return null;
  }

  if (!caseWorkflow || !caseWorkflow.questions || caseWorkflow.questions.length === 0) {
    return null;
  }

  const hasValidQuestions = caseWorkflow.questions.some(
    wq => (wq.question.answers && wq.question.answers.length > 0) ||
           wq.question.questionType === 'SPAN' || wq.question.questionType === 'SPANS'
  );
  if (!hasValidQuestions) {
    return null;
  }

  const boxProps = {
    className: "case-questions-form",
    p: 5,
    bg: "#FEEEEA",
    borderRadius: "md",
    border: "1px solid",
    borderColor: "#c37c6a",
  } as const;

  if (isAttributesLoading) {
    return (
      <Box {...boxProps}>
        <Text fontSize={pxToRem(14)} fontWeight="semibold" color="gray.700" mb={1}>
          Case Questions
        </Text>
        <Divider mb={3} />
        <Center py={4}>
          <Spinner size="md" color="#c37c6a" />
        </Center>
      </Box>
    );
  }

  return (
    <Box {...boxProps}>
      <Text fontSize={pxToRem(14)} fontWeight="semibold" color="gray.700" mb={3}>
        Case Questions
      </Text>
      <Divider mb={3} />
      {error ? (
        <Alert status="error" variant="left-accent" borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Unable to load case questions. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      ) : isEvaluationLoading && !evaluation ? (
        <Center py={4}>
          <Spinner size="md" color="#c37c6a" />
        </Center>
      ) : (
        <VStack spacing={3} align="stretch">
          {visibleQuestions.map((workflowQuestion) => {
              const currentAnswer = findAnswerForQuestion(
                attributes,
                workflowQuestion.question.id
              );

              return (
                <React.Fragment key={workflowQuestion.question.id}>
                  <RubricQuestionField
                    workflowQuestion={workflowQuestion}
                    currentAnswer={currentAnswer}
                    onAnswerChange={(value) =>
                      handleAnswerChange(workflowQuestion.question.id, value)
                    }
                    isDisabled={isDisabled || isEvaluationLoading}
                    isAutofilled={autofilledAnswersMap.has(workflowQuestion.question.id)}
                    filteredAnswerIds={filteredAnswersMap.get(workflowQuestion.question.id)}
                    isEvaluating={isEvaluationLoading && lastChangedQuestionId === workflowQuestion.question.id}
                    spans={allSpans}
                  />
                </React.Fragment>
              );
            })}
        </VStack>
      )}
    </Box>
  );
};

export default CaseQuestionsForm;
